// icons.jsx — the Command Palette's own consistent line-art icon set.
// WL (Fable Hail Mary Wave 5). Every icon here is a 20×20 stroke-only SVG drawn
// on `currentColor` at a uniform 1.6px stroke, so the whole palette reads as ONE
// icon family (the repo's existing icons are a mix of sizes/fills — using them
// here would break alignment + visual rhythm). Line-art only, never emoji.
// All are decorative (aria-hidden) — the row's text label is the accessible name.

import React from "react";

// `rest` is spread FIRST and the a11y attributes are pinned AFTER it, so a
// caller can add a className / data-testid but can never un-hide an icon or
// make it tab-focusable — the whole family stays decorative by construction.
const Svg = ({ children, size = 20, ...rest }) => (
  <svg
    {...rest}
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const IconSearch = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="9" r="5.5" />
    <path d="M13.5 13.5 L17 17" />
  </Svg>
);

export const IconCreateWheel = (p) => (
  <Svg {...p}>
    <circle cx="8" cy="8" r="5.5" />
    <path d="M8 5.5 V10.5 M5.5 8 H10.5" />
    <path d="M14.5 13.5 V17.5 M12.5 15.5 H16.5" />
  </Svg>
);

export const IconExplore = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="10" r="7.5" />
    <path d="M13 7 L11 11 L7 13 L9 9 Z" />
  </Svg>
);

export const IconGallery = (p) => (
  <Svg {...p}>
    <rect x="3" y="3" width="6" height="6" rx="1.4" />
    <rect x="11" y="3" width="6" height="6" rx="1.4" />
    <rect x="3" y="11" width="6" height="6" rx="1.4" />
    <rect x="11" y="11" width="6" height="6" rx="1.4" />
  </Svg>
);

export const IconSettings = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="10" r="2.4" />
    <path d="M10 2.5v2.2M10 15.3v2.2M17.5 10h-2.2M4.7 10H2.5M15.3 4.7l-1.6 1.6M6.3 13.7l-1.6 1.6M15.3 15.3l-1.6-1.6M6.3 6.3 4.7 4.7" />
  </Svg>
);

export const IconStorage = (p) => (
  <Svg {...p}>
    <ellipse cx="10" cy="5" rx="6.5" ry="2.5" />
    <path d="M3.5 5v10c0 1.38 2.91 2.5 6.5 2.5s6.5-1.12 6.5-2.5V5" />
    <path d="M3.5 10c0 1.38 2.91 2.5 6.5 2.5s6.5-1.12 6.5-2.5" />
  </Svg>
);

export const IconBilling = (p) => (
  <Svg {...p}>
    <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
    <path d="M2.5 8.5h15" />
    <path d="M5.5 12.5h3" />
  </Svg>
);

export const IconHelp = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="10" r="7.5" />
    <path d="M8 8a2 2 0 1 1 2.7 1.87c-.5.2-.7.6-.7 1.13v.5" />
    <circle cx="10" cy="14" r="0.4" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconSegment = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 10 L10 2.5 M10 10 L16.5 13.5" />
  </Svg>
);

// Small keycap glyphs for the footer hint row.
export const IconEnter = (p) => (
  <Svg {...p}>
    <path d="M15 5v5H6" />
    <path d="M8.5 7.5 6 10l2.5 2.5" />
  </Svg>
);

export const IconArrowUpDown = (p) => (
  <Svg {...p}>
    <path d="M7 8 L7 3 M5 5 L7 3 L9 5" />
    <path d="M13 12 L13 17 M11 15 L13 17 L15 15" />
  </Svg>
);

// The row-level "go" chevron shown on the active option.
export const IconGo = (p) => (
  <Svg {...p}>
    <path d="M7 4 L13 10 L7 16" />
  </Svg>
);

// Maps an action key to its icon component (single source of truth).
export const ACTION_ICONS = {
  "create-wheel": IconCreateWheel,
  explore: IconExplore,
  gallery: IconGallery,
  settings: IconSettings,
  storage: IconStorage,
  billing: IconBilling,
  help: IconHelp,
};
